import Modifier from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';

function cleanup(instance) {
  window.removeEventListener('message', instance.handleMessage);
}

/**
 * Bridges an embedded app to its host frame.
 *
 * Two things the framed app cannot do for itself:
 *
 * - **Context.** It learns its tenant, folder and bucket from us. It announces
 *   itself once with `IFRAME_READY`; anything that changes later (`search` is a
 *   tracked query param) has to be pushed, which is why `modify` re-posts.
 * - **Credentials.** It holds no token and mints nothing. Each time it needs
 *   AWS credentials it asks, and `onCredentialsRequest` answers from
 *   `S3VDRClient`. See
 *   `docs/superpowers/specs/2026-08-28-vdr-iframe-credentials-design.md`.
 *
 * A class modifier rather than a function one specifically so `iframeOrigin`
 * survives argument changes — a function modifier's closure is rebuilt on
 * every run, losing the handshake.
 */
class WindowPostMessageModifier extends Modifier {
  constructor(owner, args) {
    super(owner, args);
    registerDestructor(this, cleanup);
  }

  element = null;
  message = null;
  allowedOrigins = [];
  onCredentialsRequest = null;

  /** Learned from the handshake; until then there is nowhere safe to post. */
  iframeOrigin = null;
  listening = false;

  modify(
    element,
    positional,
    { message, allowedOrigins = [], onCredentialsRequest },
  ) {
    this.element = element;
    this.message = message;
    this.allowedOrigins = allowedOrigins;
    this.onCredentialsRequest = onCredentialsRequest;

    if (!this.listening) {
      window.addEventListener('message', this.handleMessage);
      this.listening = true;
    }

    // No-op before the handshake, a push afterwards.
    this.post({ type: 'vdr-context', message: this.message });
  }

  post(payload) {
    if (!this.iframeOrigin) return;
    this.element?.contentWindow?.postMessage(payload, this.iframeOrigin);
  }

  handleMessage = async (event) => {
    // Origin alone is not enough: any window at an allow-listed origin that
    // holds a handle to this page (e.g. via `window.open`) could otherwise
    // send `IFRAME_READY` and hijack `iframeOrigin`, after which every reply
    // is posted at a `targetOrigin` the real iframe never receives. Checked
    // first so a foreign source can't reach the origin check's side effects
    // either.
    if (event.source !== this.element?.contentWindow) return;
    if (!this.allowedOrigins.includes(event.origin)) return;

    if (event.data?.type === 'IFRAME_READY') {
      this.iframeOrigin = event.origin;
      this.post({ type: 'vdr-context', message: this.message });
      return;
    }

    if (event.data?.type === 'vdr-credentials-request') {
      const { requestId } = event.data;
      try {
        const credentials = await this.onCredentialsRequest();
        this.post({ type: 'vdr-credentials', requestId, credentials });
      } catch (error) {
        // Answered rather than swallowed: the framed app is awaiting this
        // requestId, and a dropped reply would hang its file browser until the
        // request timed out.
        this.post({
          type: 'vdr-credentials-error',
          requestId,
          error: error?.message ?? String(error),
        });
      }
    }
  };
}

export default WindowPostMessageModifier;
